/**
 * MoveToProjectModal — pick a project (or Inbox) to move a flow into.
 * Used from the FlowCard "Move to project…" context menu item.
 */

import React from 'react';
import PaletteIcon from '@mui/icons-material/Palette';
import type { Project, InboxProjectId } from '../../types/project';
import { INBOX_PROJECT_ID } from '../../types/project';

export interface MoveToProjectModalProps {
  open: boolean;
  /** Project the flow is currently in (or INBOX_PROJECT_ID for Inbox). */
  currentProjectId?: string | InboxProjectId | null;
  projects: Project[];
  onClose: () => void;
  /** Called with the target project id, or INBOX_PROJECT_ID for Inbox. */
  onSelect: (target: string | InboxProjectId) => void;
  /** Title override — used by bulk-move ("Move N Knods"). */
  title?: string;
}

export const MoveToProjectModal: React.FC<MoveToProjectModalProps> = ({
  open,
  currentProjectId,
  projects,
  onClose,
  onSelect,
  title = 'Move to project',
}) => {
  if (!open) return null;

  const renderItem = (
    key: string,
    label: React.ReactNode,
    isCurrent: boolean,
    onClick: () => void,
    leadingIcon?: React.ReactNode,
  ) => (
    <button
      key={key}
      type="button"
      onClick={() => {
        if (isCurrent) return;
        onClick();
      }}
      disabled={isCurrent}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        width: '100%',
        background: isCurrent ? '#1a2a2a' : 'transparent',
        border: '1px solid #2a2a2a',
        borderRadius: 8,
        color: isCurrent ? '#666' : '#fff',
        cursor: isCurrent ? 'default' : 'pointer',
        padding: '10px 14px',
        fontSize: 14,
        textAlign: 'left',
        transition: 'background 0.1s ease',
      }}
      onMouseEnter={(e) => {
        if (!isCurrent) (e.currentTarget as HTMLButtonElement).style.background = '#222';
      }}
      onMouseLeave={(e) => {
        if (!isCurrent) (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
      }}
    >
      {leadingIcon}
      <span style={{ flex: 1 }}>{label}</span>
      {isCurrent && <span style={{ fontSize: 11, color: '#666' }}>Current</span>}
    </button>
  );

  return (
    <div className="delete-confirm-overlay" onClick={onClose}>
      <div className="delete-confirm-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 440 }}>
        <h3>{title}</h3>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 12, maxHeight: 360, overflowY: 'auto' }}>
          {renderItem(
            'default-bucket',
            'General',
            currentProjectId === INBOX_PROJECT_ID || currentProjectId == null,
            () => onSelect(INBOX_PROJECT_ID),
            <PaletteIcon style={{ fontSize: 18, color: '#aaa' }} />,
          )}
          {projects.map((p) =>
            renderItem(
              p.id,
              <span>
                {p.emoji && <span style={{ marginRight: 6 }}>{p.emoji}</span>}
                {p.name}
              </span>,
              currentProjectId === p.id,
              () => onSelect(p.id),
              <span
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  background: p.color || '#42C3D7',
                  flexShrink: 0,
                }}
              />,
            ),
          )}
          {projects.length === 0 && (
            <p style={{ color: '#888', fontSize: 12, margin: '8px 4px 0' }}>
              No projects yet. Close this and click "New project" on the dashboard.
            </p>
          )}
        </div>

        <div className="delete-confirm-actions" style={{ marginTop: 16 }}>
          <button className="delete-confirm-cancel" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default MoveToProjectModal;
